import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Field, FilePicker, InfoTip, inputCls, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, useFieldInvalid, FieldInvalidInline, INVALID_FIELD_ATTR } from '../components/shared/formValidation';
import { fieldInvalidRing, fieldInvalidLabel } from '../components/workflow/constants';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import QcReviewerPicks, {
  planFor, reviewerPicksOf, reviewerPickGaps, appendReviewerPicks,
} from '../components/workflow/QcReviewerPicks';
import { FieldCommentInline } from '../components/workflow/fieldComments';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { qcAnswerGaps, qcSplitGaps, QC_TYPE_LABEL, isVisible, fmtQcAnswerValue } from './qcQuestions';
import { useQcPrefill } from '../components/workflow/QcPrefill';
import { QcForm } from '../components/workflow/QcForm';

// QC6 / QC7 acceptance — submission form (docs/modules/workflow-qc.md).
// ONE entry — pick the registered ENGAGEMENTS the
// acceptance covers (one or several of the same client group + FYE year), then choose
// QC6 (new client) or QC7 (existing client) yourself; the type locks on
// submission. FORM-ONLY: the questionnaire IS the submission (no signed-
// document upload); files[] are supporting documents.
//
// DRAFTS (0146): the form is long and rarely finished in one sitting, so
// "Save draft" keeps it (status 'draft': private to the requester, in no
// review queue, no mail, nothing reserved). The detail page renders THIS
// component for a draft (`draft` prop) — Save draft / Submit / Delete draft.
// The type locks as soon as a draft exists (a different type = a new form).
// REVIEWERS: the submitter names them from the Partner/Director roster; HOW
// MANY and of WHICH GRADE follow the form's own answers — engagement type,
// non-assurance service type, and for a QC7 whether the risk rating moved
// from prior year (qcQuestions.qcReviewerPlanOf; pickers in QcReviewerPicks).

// A pick-one radio row (Yes/No and every 'radio' question) — no dropdown, one
// click to answer. Long option labels wrap onto their own line.
const RadioChoices = ({ q, value, onChange, options }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-0.5">
    {options.map((o) => (
      <label key={o} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="radio" name={q.id} className="mt-0.5" checked={value === o} onChange={() => onChange(o)} />
        <span>{o}</span>
      </label>
    ))}
  </div>
);

// A 'computed' question: derived from other answers, shown read-only. The
// derived value is written back into `answers` so read-only views and History
// diffs show exactly what the submitter saw.
const ComputedValue = ({ q, value, onChange, answers }) => {
  const computed = q.compute ? q.compute(answers) : '';
  useEffect(() => {
    if ((value === undefined ? '' : String(value)) !== computed) onChange(computed);
  }, [computed]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-100 text-sm text-slate-800">
      {computed === '' ? '—' : `${computed} %`}
    </div>
  );
};

// One questionnaire field, rendered from its question spec. `answers` is the
// whole answer map so conditional (showIf) questions can decide to render.
// `locked` = shown, not editable.
export const QuestionField = ({ q, value, onChange, answers = {}, locked = false }) => {
  // The checkbox row is not a Field, so it marks itself the way one would.
  const invalid = useFieldInvalid(`answers.${q.id}`);
  if (q.type === 'section') {
    return (
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-3 mt-1 border-t border-slate-200 first:border-t-0 first:pt-0 first:mt-0">
        {q.label}
      </h3>
    );
  }
  if (!isVisible(q, answers)) return null;
  if (locked && q.type !== 'checkbox') {
    return (
      <Field label={q.label} required={q.required} hint={q.hint} field={`answers.${q.id}`}>
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
          <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title="Comes from the linked engagement — correct it through an Engagement Amendment" />
          <span className="text-sm font-semibold text-slate-900">{fmtQcAnswerValue(q, value)}</span>
          <span className="text-sm text-slate-500">from the engagement</span>
        </div>
      </Field>
    );
  }
  // 退件意見貼在它講的那一題底下。key 與 History 的
  // 逐題差異同一組（qcQuestions.expandAnswerChanges 寫的 `answers.<id>`），
  // 所以一句意見在詳情頁、Modify 表單、信件三處指的都是同一題。
  const commentField = `answers.${q.id}`;
  if (q.type === 'checkbox') {
    return (
      <div className={invalid ? fieldInvalidRing : undefined} {...(invalid ? { [INVALID_FIELD_ATTR]: commentField } : {})}>
        <label className={`flex items-start gap-2 text-sm ${invalid ? fieldInvalidLabel : 'text-slate-800'}`}>
          <input type="checkbox" checked={value === true} onChange={e => onChange(e.target.checked)} className="mt-0.5" />
          <span>{q.label}{q.required ? ' *' : ''}</span>
        </label>
        <FieldInvalidInline field={commentField} text="Required — please tick the declaration" />
        <FieldCommentInline field={commentField} />
      </div>
    );
  }
  return (
    <Field label={q.label} required={q.required} hint={q.hint} field={commentField}>
      {q.type === 'yesno' ? (
        <RadioChoices q={q} value={value} onChange={onChange} options={['Yes', 'No']} />
      ) : q.type === 'radio' ? (
        <RadioChoices q={q} value={value} onChange={onChange} options={q.options || []} />
      ) : q.type === 'computed' ? (
        <ComputedValue q={q} value={value} onChange={onChange} answers={answers} />
      ) : q.type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}>
          <option value="">Select…</option>
          {q.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : q.type === 'textarea' ? (
        <textarea rows="2" value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls} />
      ) : q.type === 'date' ? (
        <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls} />
      ) : (
        <input type={q.type === 'number' ? 'number' : 'text'} value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls} />
      )}
    </Field>
  );
};

// The whole acceptance form, shared by every surface that EDITS answers
// (New page, Modify modal, QCA change form, Management records form).
// `engagements` = the covered engagements in slim-list shape (first = primary:
// it feeds the header).
// `primaryEngagement` is accepted for older call sites.
// `feePerEngagement`: every engagement's own Estimated fees (a new form; an
// existing one once its fee is changed).
export const QcAnswersForm = ({ qcType, answers, onChange, engagements = null, primaryEngagement = null, addendumOnly = false, feePerEngagement = false }) => {
  const list = engagements || (primaryEngagement ? [primaryEngagement] : []);
  if (!qcType) return null;
  return <QcForm qcType={qcType} answers={answers || {}} onChange={onChange} engagements={list} addendumOnly={addendumOnly} feePerEngagement={feePerEngagement} />;
};

const WorkflowQcNew = ({ draft = null, onSaved = null, onSubmitted = null, onDeleted = null }) => {
  const navigate = useNavigate();
  const isDraft = !!draft;

  const [engagements, setEngagements] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [engagementIds, setEngagementIds] = useState(
    draft ? (draft.engagements || []).map((e) => e.engagementId).filter(Boolean) : []
  );
  const [qcType, setQcType] = useState(draft ? draft.qcType : '');
  const [picks, setPicks] = useState(() => reviewerPicksOf(draft));
  const [answers, setAnswers] = useState(draft ? (draft.answers || {}) : {});
  // Who must review follows the answers (engagement type, non-assurance
  // service type, QC7 rating change) — recomputed as the form is filled.
  const plan = useMemo(() => (qcType ? planFor(answers, qcType) : null), [answers, qcType]);
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState(draft ? (draft.remarks || '') : '');
  const { removeIds, toggle: toggleRemove, appendTo } = useRemovableFiles();

  useEffect(() => {
    (async () => {
      try {
        // requestId puts the draft's own coverage back into the feed (its
        // engagements are not reserved, so somebody else's submission may
        // have taken one out of the free list — the draft still has to show
        // what it covers).
        const data = await fetchJSON(`/api/workflow/qc/options${draft ? `?requestId=${draft.id}` : ''}`);
        const own = draft
          ? (draft.engagements || [])
            .filter((e) => e.engagementId && !(data.engagements || []).some((x) => x.id === e.engagementId))
            .map((e) => ({
              id: e.engagementId, engagementName: e.engagementName, entityName: e.entityName, yearEnd: e.yearEnd,
              serviceProvided: e.serviceProvided, engagementSeq: e.engagementSeq, serviceDescription: e.serviceDescription,
              sourceRequestNo: null, partners: [],
            }))
          : [];
        setEngagements([...(data.engagements || []), ...own]);
      } catch (e) { setError(e.message); }
    })();
  }, [draft]);

  const selected = useMemo(
    () => engagementIds.map((id) => engagements.find((e) => e.id === id)).filter(Boolean),
    [engagements, engagementIds]
  );
  // 一案一張在途單（契約 §2g）：選到的案有別型單在審就停用 Submit——草稿照存。
  const crossBlocked = engagementBlockReasons(selected, 'qc').length > 0;

  // 起始值：PIE／費用／活動從涵蓋案帶入；Registration 若答 Yes 做 screening，
  // TNE／TNA 也帶（都可改）。
  useQcPrefill({ engagements: selected, qcType, answers, setAnswers });

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  // The questionnaire rows mark themselves from the `answers.<id>` keys; they
  // only count once a type is chosen (no type ⇒ no form on screen).
  const gaps = useMemo(() => {
    const g = [];
    if (engagementIds.length === 0) g.push({ key: 'engagements', label: 'Engagement (At Least One)' });
    if (!qcType) g.push({ key: 'qcType', label: 'QC Type (QC6 or QC7)' });
    if (qcType) g.push(...qcAnswerGaps(answers, qcType), ...qcSplitGaps(answers, qcType, engagementIds, { requireFee: true }));
    if (qcType) g.push(...reviewerPickGaps(plan, picks));
    return g;
  }, [engagementIds, qcType, plan, picks, answers]);
  const validation = useFormValidation(gaps);

  const buildForm = () => {
    const form = new FormData();
    form.set('engagementIds', JSON.stringify(engagementIds));
    form.set('qcType', qcType);
    appendReviewerPicks(form, plan, picks);
    form.set('answers', JSON.stringify(answers));
    form.set('remarks', remarks);
    files.forEach((f) => form.append('files', f));
    if (isDraft) appendTo(form);
    return form;
  };

  // Save draft — type + coverage are all it needs (the server keeps the
  // coverage rules: same group + FYE year, not already accepted).
  const saveDraft = async () => {
    const missing = [];
    if (engagementIds.length === 0) missing.push('Engagement (At Least One)');
    if (!qcType) missing.push('QC Type (QC6 or QC7)');
    if (missing.length > 0) { setError(`A draft needs at least: ${missing.join(', ')}`); return; }
    setSaving(true); setError(null); setNotice(null);
    try {
      const form = buildForm();
      form.set('draft', 'true');
      if (isDraft) {
        await fetchJSON(`/api/workflow/qc/${draft.id}`, { method: 'PATCH', body: form });
        setFiles([]);
        setNotice('Draft saved. It stays private until you submit it.');
        if (onSaved) onSaved();
      } else {
        const data = await fetchJSON('/api/workflow/qc', { method: 'POST', body: form });
        navigate(`/workflow/qc/${data.request.id}`);
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    // Marks the empty fields and scrolls to the first; the banner comes from
    // `validation.error` and clears itself as the gaps are filled.
    if (!validation.validate()) { setError(null); return; }

    setSaving(true);
    setError(null);
    try {
      if (isDraft) {
        // Save what is on screen, then promote the draft — the server re-runs
        // the full validation and the duplicate check on submit.
        const form = buildForm();
        form.set('draft', 'true');
        await fetchJSON(`/api/workflow/qc/${draft.id}`, { method: 'PATCH', body: form });
        await fetchJSON(`/api/workflow/qc/${draft.id}/submit`, { method: 'POST' });
        if (onSubmitted) onSubmitted();
        else navigate(`/workflow/qc/${draft.id}`);
      } else {
        const data = await fetchJSON('/api/workflow/qc', { method: 'POST', body: buildForm() });
        navigate(`/workflow/qc/${data.request.id}`);
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/qc"
      backLabel="QC6 / QC7 Forms"
      title={isDraft ? `${draft.requestNo} — Draft ${draft.qcType}` : 'New QC6 / QC7 Forms Request'}
      subtitle={isDraft
        ? 'Draft: only you can see it until you submit.'
        : 'Client acceptance (QC6) or continuance (QC7).'}
      error={error || validation.error}
      notice={notice}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={crossBlocked}
      actionsLeft={isDraft ? (
        <button type="button" onClick={() => setConfirmingDelete(true)} disabled={saving}
          className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">
          Delete Draft
        </button>
      ) : null}
      actionsExtra={(
        <button type="button" onClick={saveDraft} disabled={saving} className={`${btnGhost} border border-slate-300`}>
          {saving ? 'Saving…' : 'Save Draft'}
        </button>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement(s)" hint={<>Engagements still awaiting their QC6 / QC7 form, grouped by client group and financial year. <InfoTip text="One submission may cover several companies of the same client group whose FYE falls in the same year — the year-end dates may differ." /></>}>
          <GroupedEngagementPicker
            engagements={engagements}
            selectedIds={engagementIds}
            onChange={setEngagementIds}
          />
          {/* The picker is not a Field — the "Required" mark belongs right under it. */}
          <FieldInvalidInline field="engagements" text="Required — pick at least one engagement" />
          <EngagementBlockers selected={selected} flow="qc" note="You can still save a draft and submit it once that request is closed." />
        </FormSection>

        <FormSection step={2} title="QC Type" hint={<>You decide: QC6 = new client acceptance, QC7 = existing client continuance. <InfoTip text="The type locks once the form is saved or submitted — a different type means a new request." /></>}>
          <div className="flex flex-wrap gap-4">
            {[['QC6', QC_TYPE_LABEL.QC6 || 'QC6 — new client acceptance'], ['QC7', QC_TYPE_LABEL.QC7 || 'QC7 — existing client continuance']].map(([v, label]) => (
              <label key={v} className={`flex items-center gap-2 text-sm ${isDraft && qcType !== v ? 'text-slate-400' : 'text-slate-700'}`}>
                <input type="radio" name="qcType" value={v} checked={qcType === v} disabled={isDraft}
                  onChange={() => {
                    setQcType(v); setAnswers({}); setError(null);
                    setPicks(reviewerPicksOf(null));
                  }} />
                {label}
              </label>
            ))}
          </div>
          <FieldInvalidInline field="qcType" text="Required — choose QC6 or QC7" />
          {isDraft && <p className="text-xs text-slate-500">The type is locked on this draft — start a new form for the other type.</p>}
        </FormSection>

        {qcType && (
          <FormSection step={3} title="QC6 / QC7 Form" hint={<>The form is the submission — every question is shown; the ones that do not apply are filled in and locked automatically. <InfoTip text="The covered engagements' partners review and approve it. The TNE / TNA answers and the EQR named here are written to every covered engagement on approval. When the registration asked for QM screening, the TNE / TNA answers start from the screening questions and stay editable. Hover a blue info icon for the guidance printed on the Word form, or click it to keep the guidance open under the question." /></>}>
            <QcAnswersForm
              qcType={qcType}
              answers={answers}
              onChange={setAnswers}
              engagements={selected}
              feePerEngagement
            />
          </FormSection>
        )}

        <FormSection step={4} title="Attachments & Remarks">
          {isDraft && (draft.files || []).length > 0 && (
            <EditableAttachments
              files={draft.files}
              removeIds={removeIds}
              onToggle={toggleRemove}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc/${draft.id}/files/${f.id}`}
              label="Files on This Draft"
            />
          )}
          <Field label={isDraft ? 'Add Supporting Files' : 'Supporting Files'} hint="AML screenshots, predecessor clearance, other evidence. PDF / DOC / DOCX / JPG / PNG.">
            <FilePicker multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setFiles(Array.from(e.target.files || []))}
              files={files} />
          </Field>
          <Field label="Remarks">
            <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        {/* Reviewer picks — the submitter names who approves. Only on screen
            once a type is chosen; the form's answers decide how many and of
            which grade (QcReviewerPicks). */}
        {qcType && (
          <FormSection step={5} title={plan && plan.stages && plan.stages.length > 1 ? 'Reviewers' : 'Reviewer'}>
            <QcReviewerPicks
              plan={plan}
              picks={picks}
              onChange={setPicks}
              qcType={qcType}
              hint="You pick who reviews this request, from the firm's roster."
            />
          </FormSection>
        )}
      </FormValidationProvider>

      {confirmingDelete && (
        <ConfirmDeleteModal
          title={`Delete ${draft.requestNo}`}
          message="This draft has not been submitted. Deleting it removes the saved answers and files. This cannot be undone."
          confirmLabel="Delete Draft"
          onClose={() => setConfirmingDelete(false)}
          onConfirm={async () => {
            await fetchJSON(`/api/workflow/qc/${draft.id}`, { method: 'DELETE' });
            if (onDeleted) onDeleted(); else navigate('/workflow/qc');
          }}
        />
      )}
    </FormShell>
  );
};

export default WorkflowQcNew;
